/**
 * The encounter recorded at an enrolment or with *Add photo to this subject* (slice 05,
 * ADR-0045, contract C7): a fresh draft, where its place comes from (the phone's fix or what
 * was typed, both with MGRS), and the request body. The rules of the draft itself live in
 * `components/humint/encounter/encounter-draft`.
 */
import {
  EMPTY_GC3,
  patchEncounter,
  showsArt17,
  toLocalIso,
} from '@/components/humint/encounter/encounter-draft'
import type {
  EncounterChoice,
  EncounterDraft,
  LocationMode,
  RecentEncounter,
} from '@/components/humint/encounter/encounter-types'
import type { CheckVerdict, EncounterType, GeoPosition } from '@/components/humint/types'
import type {
  CaptureUpload,
  EncounterDto,
  EncounterJoinDto,
  EncounterTypeDto,
  FaceCheckVerdictKind,
  Gc3ParticularsDto,
} from '@/services/api/humint'
import type { EncounterLinkRequest } from '@/services/api/humint-encounters'
import { parseTypedLocation } from '../geo/location-input'
import { toMgrs } from '../geo/mgrs'

/** A position from `navigator.geolocation`. */
export interface DeviceFix {
  lat: number
  lon: number
  accuracyM: number | null
}

export interface NewEncounterOptions {
  now: Date
  type: EncounterType
  /** The capturing unit to prefill; null leaves the field empty. */
  unit: string | null
  /** `device` on the phone (GPS at once), `manual` on the desktop (typed place first). */
  mode: LocationMode
  checkVerdictKind: CheckVerdict['kind'] | null
}

/** A draft as the sheet opens: now, no place yet, the unit prefilled when there is one. */
export function newEncounterDraft(options: NewEncounterOptions): EncounterDraft {
  const unit = options.unit?.trim() || null
  const draft: EncounterDraft = {
    type: options.type,
    at: toLocalIso(options.now),
    atIsAuto: true,
    location: null,
    locationMode: options.mode,
    manualGrid: '',
    placeNote: null,
    unit,
    unitPrefilled: unit !== null,
    circumstances: [],
    circumstancesNote: null,
    captureTag: null,
    legalStatus: null,
    gc3: null,
    checkVerdictKind: options.checkVerdictKind,
    companions: [],
  }
  return patchEncounter(draft, {})
}

/** The phone's fix as the encounter's place, with its MGRS. */
export function devicePosition(fix: DeviceFix): GeoPosition {
  return {
    lat: fix.lat,
    lon: fix.lon,
    accuracyM: fix.accuracyM,
    mgrs: toMgrs(fix.lat, fix.lon) ?? '',
    source: 'device',
  }
}

/** The place a draft holds: the typed one in manual mode, else the phone's latest fix. */
export function placeOf(draft: EncounterDraft, fix: DeviceFix | null): GeoPosition | null {
  if (draft.locationMode === 'manual') {
    const typed = parseTypedLocation(draft.manualGrid ?? '')
    return typed && { lat: typed.lat, lon: typed.lon, mgrs: typed.mgrs, source: 'manual' }
  }
  return fix ? devicePosition(fix) : null
}

/**
 * A change from the form, with its place brought up to date: switching between GPS and a
 * typed place, or typing, re-reads where the encounter happened.
 */
export function reconcileEncounter(
  previous: EncounterDraft,
  next: EncounterDraft,
  fix: DeviceFix | null
): EncounterDraft {
  const moved =
    next.locationMode !== previous.locationMode || next.manualGrid !== previous.manualGrid
  return moved ? { ...next, location: placeOf(next, fix) } : next
}

/** A new fix from the phone: the place follows it unless the operator typed one. */
export function withDeviceFix(draft: EncounterDraft, fix: DeviceFix): EncounterDraft {
  return draft.locationMode === 'manual' ? draft : { ...draft, location: devicePosition(fix) }
}

const RECORDABLE: readonly EncounterTypeDto[] = [
  'detention',
  'checkpoint',
  'debriefing',
  'engagement',
]

const isRecordable = (type: EncounterType): type is EncounterTypeDto =>
  RECORDABLE.some(recordable => recordable === type)

const trimmed = (value: string | null | undefined) => value?.trim() || null

function toGc3Dto(gc3: EncounterDraft['gc3']): Gc3ParticularsDto | null {
  const particulars = gc3 ?? EMPTY_GC3
  const dto: Gc3ParticularsDto = {
    name_as_given: trimmed(particulars.nameAsGiven),
    rank: trimmed(particulars.rank),
    date_of_birth: trimmed(particulars.dateOfBirth),
    service_number: trimmed(particulars.serviceNumber),
  }
  return Object.values(dto).some(value => value !== null) ? dto : null
}

/** The dormant `strong` band is still a possible match to the audit trail. */
function checkVerdictOf(draft: EncounterDraft): FaceCheckVerdictKind | null {
  const kind = draft.checkVerdictKind ?? null
  return kind === 'strong' ? 'possible' : kind
}

/**
 * The encounter as C7 sends it, or null for a type that cannot be recorded yet. Blank text
 * goes out as null; the capture tag and legal status only for a detention; the Art. 17
 * card only for a detention or a prisoner of war, and only when something was written in it.
 */
export function toEncounterDto(draft: EncounterDraft, encounterId: string): EncounterDto | null {
  if (!isRecordable(draft.type)) return null
  const detention = draft.type === 'detention'
  const { location } = draft
  return {
    encounter_id: encounterId,
    type: draft.type,
    occurred_at: draft.at,
    location: location
      ? {
          lat: location.lat,
          lon: location.lon,
          accuracy_m: location.accuracyM ?? null,
          source: location.source,
        }
      : null,
    place_note: trimmed(draft.placeNote),
    capturing_unit: trimmed(draft.unit),
    circumstances: [...draft.circumstances],
    circumstances_note: trimmed(draft.circumstancesNote),
    capture_tag: detention ? trimmed(draft.captureTag) : null,
    legal_status: detention ? (draft.legalStatus ?? null) : null,
    gc3: showsArt17(draft) ? toGc3Dto(draft.gc3) : null,
    check_verdict: checkVerdictOf(draft),
  }
}

/**
 * Joining `encounter` (slice 06): its ref and this subject's own values from the draft — the tag,
 * the legal status and the Art. 17 card only for a detention (the card only when something was
 * written in it), and the verdict of the check that led here.
 */
export function toJoinDto(encounter: RecentEncounter, draft: EncounterDraft): EncounterJoinDto {
  const detention = encounter.type === 'detention'
  return {
    encounter_ref: encounter.ref,
    capture_tag: detention ? trimmed(draft.captureTag) : null,
    legal_status: detention ? (draft.legalStatus ?? null) : null,
    check_verdict: checkVerdictOf(draft),
    gc3: detention ? toGc3Dto(draft.gc3) : null,
  }
}

/**
 * The draft as the chosen encounter reads it: joining one, the draft takes its type, so the tag,
 * legal status and Art. 17 rules are that encounter's.
 */
export function draftFor(choice: EncounterChoice | null, draft: EncounterDraft): EncounterDraft {
  if (choice?.kind !== 'join' || draft.type === choice.encounter.type) return draft
  return patchEncounter(draft, { type: choice.encounter.type })
}

/**
 * The encounter part of every request that records one (slices 05–07): the link body, an
 * enrolment's, an *Add photo* capture's. A new encounter as C7 sends it, or the join with the
 * subject's own values; null for a type that cannot be recorded yet.
 */
export function toLinkRequest(
  choice: EncounterChoice,
  draft: EncounterDraft,
  encounterId: string
): EncounterLinkRequest | null {
  if (choice.kind === 'none') return null
  if (choice.kind === 'join') return toJoinDto(choice.encounter, draftFor(choice, draft))
  const encounter = toEncounterDto(draft, encounterId)
  return encounter && { encounter }
}

/** The encounter part opens a new encounter; otherwise it joins one that exists. */
export function opensEncounter(part: EncounterLinkRequest): part is { encounter: EncounterDto } {
  return 'encounter' in part
}

/** Where the photo was taken, for the capture upload: the encounter's place. */
export function captureLocation(draft: EncounterDraft): CaptureUpload['location'] {
  const { location } = draft
  return location
    ? { lat: location.lat, lon: location.lon, accuracyM: location.accuracyM ?? null }
    : null
}
